import React, { useState, useEffect } from 'react';
import { CrudForm, CrudStyles } from '../shared/Crud';
import MessageModal from '../shared/MessageModal';
import { maskCPF, maskPhone } from '../../utils/masks';

const { FormGroup, Input } = CrudStyles;

function formatPhoneDisplay(pn) {
    if (!pn) return '';
    if (typeof pn === 'string') return pn;
    const area = pn.areaCode ?? '';
    const num = pn.number ?? '';
    if (area && num) return `(${area}) ${num.length === 9 ? num.replace(/(\d{5})(\d{4})/, '$1-$2') : num}`;
    return num || area || '';
}

export default function ResponsibleReceiptForm({ initialData, onSubmit, onCancel }) {
    const isEdit = Boolean(initialData?.id);
    const [formData, setFormData] = useState({
        name: '',
        document: '',
        phoneNumber: '',
        active: true,
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });

    useEffect(() => {
        if (initialData) {
            const phone = initialData.phoneNumber != null
                ? formatPhoneDisplay(initialData.phoneNumber)
                : (initialData.phone ?? '');
            setFormData({
                name: initialData.name ?? '',
                document: initialData.document ?? '',
                phoneNumber: phone,
                active: initialData.active !== false,
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let next = value;
        if (name === 'document') next = maskCPF(value);
        if (name === 'phoneNumber') next = maskPhone(value);
        setFormData((prev) => ({ ...prev, [name]: next }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        setErrors({});
        const name = (formData.name ?? '').trim();
        if (name.length < 2) {
            setErrors({ name: 'Nome deve ter entre 2 e 100 caracteres.' });
            return;
        }
        if (name.length > 100) {
            setErrors({ name: 'Nome deve ter no máximo 100 caracteres.' });
            return;
        }
        setIsSubmitting(true);
        try {
            if (isEdit) {
                await onSubmit({
                    id: initialData.id,
                    name: name,
                    phoneNumber: formData.phoneNumber?.trim() || null,
                    active: formData.active,
                });
            } else {
                await onSubmit({
                    name: name,
                    document: formData.document?.replace(/\D/g, '') || null,
                    phoneNumber: formData.phoneNumber?.trim() || null,
                });
            }
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao salvar.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                />
            )}
            <CrudForm
            title={isEdit ? 'Editar responsável pelo recebimento' : 'Novo responsável pelo recebimento'}
            onSubmit={handleSubmit}
            onCancel={onCancel}
            isSubmitting={isSubmitting}
        >
            <FormGroup>
                <label htmlFor="name">Nome *</label>
                <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Nome completo (2 a 100 caracteres)"
                    maxLength={100}
                    $hasError={!!errors.name}
                    title={errors.name}
                />
                {errors.name && <span style={{ color: '#dc2626', fontSize: '12px' }}>{errors.name}</span>}
            </FormGroup>
            <FormGroup>
                <label htmlFor="document">CPF</label>
                <Input
                    id="document"
                    name="document"
                    value={formData.document}
                    onChange={handleChange}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    disabled={isEdit}
                    readOnly={isEdit}
                    title={isEdit ? 'Documento não pode ser alterado na edição' : ''}
                />
            </FormGroup>
            <FormGroup>
                <label htmlFor="phoneNumber">Telefone</label>
                <Input
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="(00) 00000-0000"
                    maxLength={15}
                />
            </FormGroup>
            {isEdit && (
                <FormGroup style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                        type="checkbox"
                        id="active"
                        name="active"
                        checked={formData.active}
                        onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                    />
                    <label htmlFor="active" style={{ marginBottom: 0 }}>Ativo</label>
                </FormGroup>
            )}
        </CrudForm>
        </>
    );
}
